import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { flappyAPI } from '../services/api';
import { flappySimSocket, getSocket } from '../services/socket';
import FlappySimCanvas from '../components/FlappySimCanvas';
import '../styles/Flappy.css';

function asNumber(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

export default function FlappySimMatch() {
  const navigate = useNavigate();
  const { matchId } = useParams();
  const { user } = useAuth();

  const [match, setMatch] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [startInfo, setStartInfo] = useState(null); // {seed,startTimeMs,serverTime}
  const [socketStatus, setSocketStatus] = useState('disconnected');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const socket = getSocket();

  const meId = asNumber(user?.id);
  const creatorId = asNumber(match?.creator_id);

  const opponentLabel = useMemo(() => {
    if (!match) return 'OPPONENT';
    if (meId && creatorId && meId === creatorId) return match.opponent_username || 'OPPONENT';
    return match.creator_username || 'OPPONENT';
  }, [creatorId, match, meId]);

  const load = async () => {
    try {
      setError('');
      const res = await flappyAPI.getMatch(matchId);
      setMatch(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load match');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    if (!socket) {
      setSocketStatus('disconnected');
      return undefined;
    }

    setSocketStatus(socket.connected ? 'connected' : 'disconnected');

    const onConnect = () => setSocketStatus('connected');
    const onDisconnect = () => setSocketStatus('disconnected');
    const onSocketError = (e) => {
      const message = e?.message || e?.toString?.() || 'Unknown socket error';
      setError((prev) => prev || `Socket error: ${message}`);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('error', onSocketError);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('error', onSocketError);
    };
  }, [matchId, socket, user?.id]);

  useEffect(() => {
    if (!socket) return undefined;

    const onClockSync = (payload) => {
      if (!payload || String(payload.matchId) !== String(matchId)) return;
      setStartInfo({
        seed: payload.seed,
        startTimeMs: payload.startTime,
        serverTime: payload.serverTime,
      });
    };

    const onMatchStart = (payload) => {
      if (!payload || String(payload.matchId) !== String(matchId)) return;
      setStartInfo({
        seed: payload.seed,
        startTimeMs: payload.startTime,
        serverTime: payload.serverTime,
      });
    };

    const onEnded = (payload) => {
      if (!payload || String(payload.matchId) !== String(matchId)) return;
      setError(`Sim ended: ${payload.reason || 'ended'}`);
    };

    const onPlayerJoined = (payload) => {
      if (!payload || String(payload.matchId) !== String(matchId)) return;
      // Helpful for debugging: confirms opponent joined the sim room.
      // eslint-disable-next-line no-console
      console.log('Sim player joined:', payload.userId);
    };

    flappySimSocket.onClockSync(onClockSync);
    flappySimSocket.onMatchStart(onMatchStart);
    flappySimSocket.onEnded(onEnded);
    flappySimSocket.onPlayerJoined(onPlayerJoined);
    return () => {
      flappySimSocket.removeListeners();
    };
  }, [matchId, socket]);

  useEffect(() => {
    if (!socket) return undefined;

    // Join only after the socket is connected (more reliable on mobile / slow networks).
    const joinNow = () => {
      // Wait a bit for auth/join handshake in the main socket service.
      const t = setTimeout(() => {
        flappySimSocket.join(matchId);
      }, 250);
      return () => clearTimeout(t);
    };

    if (socket.connected) {
      return joinNow();
    }

    const onConnect = () => {
      joinNow();
    };
    socket.once('connect', onConnect);
    return () => {
      socket.off('connect', onConnect);
    };
  }, [matchId, socket]);

  if (loading) {
    return (
      <div className="flappy-page">
        <div className="flappy-hint">Loading…</div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="flappy-page">
        <div className="flappy-error">Match not found</div>
        <button className="flappy-btn" onClick={() => navigate('/flappy/duel')}>← Lobby</button>
      </div>
    );
  }

  return (
    <div className="flappy-page">
      <div className="flappy-header">
        <div>
          <h1 className="flappy-title">🐦 Flappy Simultaneous (Beta) • Match #{match.id}</h1>
          <div className="flappy-subtitle">Input-sync (Socket.IO) • Deterministic pipes • Mobile-friendly</div>
        </div>
        <div className="flappy-header-actions">
          <button className="flappy-btn" onClick={() => navigate(`/flappy/duel/${match.id}`)}>← Back</button>
          <button className="flappy-btn" onClick={load}>Refresh</button>
        </div>
      </div>

      {error && <div className="flappy-error">{error}</div>}

      {match.status !== 'in_progress' && (
        <div className="flappy-hint">This match is not in progress yet. Ask your opponent to join the duel first.</div>
      )}

      <div className="flappy-meta" style={{ marginBottom: '1rem' }}>
        <div>
          <strong>Socket Status:</strong>{' '}
          <span style={{ color: socketStatus === 'connected' ? 'green' : 'red' }}>
            {socketStatus.toUpperCase()}
          </span>
        </div>
        <div><strong>Match Status:</strong> {match.status}</div>
      </div>

      <div className="flappy-grid">
        <section className="flappy-card">
          <h2>Players</h2>
          <div className="flappy-meta">
            <div><strong>You:</strong> {user?.username || meId || '—'}</div>
            <div><strong>Opponent:</strong> {opponentLabel}</div>
            <div><strong>Socket:</strong> {socketStatus}</div>
            <div><strong>Start:</strong> {startInfo?.startTimeMs ? new Date(startInfo.startTimeMs).toLocaleTimeString() : 'pending…'}</div>
          </div>
          <div className="flappy-hint">Tip: both players should open this page before start time.</div>
        </section>

        <section className="flappy-card">
          <h2>Play</h2>
          {!startInfo?.seed && (
            <div className="flappy-hint">Waiting for server seed…</div>
          )}
          {startInfo?.seed && (
            <FlappySimCanvas
              seed={startInfo.seed}
              socket={socket}
              matchId={matchId}
              startTimeMs={startInfo.startTimeMs}
              durationSeconds={asNumber(match?.duration_seconds || 60)}
              localPlayerLabel="YOU"
              opponentPlayerLabel={opponentLabel}
              disabled={match.status !== 'in_progress'}
              onFinish={async ({ score, endedReason, endedAtMs, flapTimes }) => {
                if (submitted || submitting) return;
                try {
                  setSubmitting(true);
                  setError('');
                  await flappyAPI.submitRun(match.id, 1, {
                    score,
                    endedReason,
                    endedAtMs,
                    flapTimes,
                  });
                  setSubmitted(true);
                  await load();
                } catch (e) {
                  setError(e.response?.data?.error || 'Failed to submit run');
                } finally {
                  setSubmitting(false);
                }
              }}
            />
          )}

          {submitting && <div className="flappy-hint">Submitting your run…</div>}
          {submitted && match?.status !== 'completed' && <div className="flappy-hint">Run submitted. Waiting for opponent…</div>}
          {match?.status === 'completed' && (
            <div className="flappy-hint">
              Match completed. Winner: {match.winner_id ? (asNumber(match.winner_id) === asNumber(user?.id) ? 'You' : opponentLabel) : 'Tie'}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
